<template>
    <div class="article">
        <h4>
            <a :href="props.article.articleIdentifiers.url" target="_blank" rel="noopener noreferrer">
                {{ props.article.articleIdentifiers.title }}
            </a>
        </h4>
        <div class="sub-title">
            {{ props.article.articleIdentifiers.date }} - {{ props.article.articleIdentifiers.source }}
        </div>
        <p class="description">{{ props.article.summary }}</p>
        <div v-if="props.showAdminControls" class="button-holder">
            <p v-if="props.article.specificInfoFailedToGenerate">PROJECT LEVEL DATA FAILED TO GENERATE</p>
            {{ article.id }}
            <div>
                Link article to another project:
                <primevue-select
                    class="different-project-selector"
                    :options="allDataStore.allRedactedDataUnfiltered.filteredProjects"
                    filter
                    optionLabel="projectName"
                    :virtualScrollerOptions="{ itemSize: 38 }"
                    v-model="additionalProjectToLink"
                />
                <primeview-button
                    label="Confirm match"
                    :disabled="!additionalProjectToLink"
                    @click="linkAdditionalProject(props.article)"
                />
            </div>
            <div>
                Add new project:
                <p-input-text v-model="titleForNewProject" placeholder="Please enter a title" />
                <primeview-button
                    label="Add new project"
                    :disabled="!titleForNewProject"
                    @click="linkNewProject(props.article)"
                />
            </div>
            <primeview-button @click="deleteArticle(article.id)">Delete article</primeview-button>
        </div>
        <div v-for="(_, index) in props.article.specificInfo" :key="index" class="scraped-project">
            <scraped-project-admin-controls
                :article="article"
                :index="index"
                :showAdminControls="showAdminControls"
                @open-project-in-modal="project => emit('open-project-in-modal', project)"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import PrimeviewButton from 'primevue/button';
import PrimevueSelect from 'primevue/select';
import PInputText from 'primevue/inputtext';
import { articleScrapingApi } from '@/api/storageTracking/ArticleScrapingApi.ts';
import { IArticle, IProjectInfo } from '@/interfaces/storageTracking/IArticle.ts';
import { useToast } from 'primevue/usetoast';
import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import { IModifySpecificInfoPostObject } from '@/interfaces/storageTracking/IModifyInfoPostObject.ts';
import { IRedactedProjectData } from '@/interfaces/storageTracking/IRedactedProjectData.ts';
import { useAllDataStore } from '@/stores/storageTracking/AllDataStore.ts';
import ScrapedProjectAdminControls from './ScrapedProjectAdminControls.vue';

const allDataStore = useAllDataStore();
const toast = useToast();

const props = defineProps<{
    article: IArticle;
    showAdminControls: boolean;
}>();

const emit = defineEmits<{
    'open-project-in-modal': [IRedactedProjectData];
}>();

const additionalProjectToLink = ref<ICsvData | null>(null);
const titleForNewProject = ref<string | null>(null);

async function linkAdditionalProject (article: IArticle): Promise<void> {
    if (!additionalProjectToLink.value) {
        toast.add({ severity: 'error', life: 10000, summary: 'Please select a project to link' });
        return;
    }
    const projectInfo: IProjectInfo = {
        projectName: additionalProjectToLink.value.projectName,
        confirmedMatch: additionalProjectToLink.value.factStorageId.toString(),
        countryCode: additionalProjectToLink.value.country,
    };
    article.specificInfo ??= [];
    article.specificInfo.push(projectInfo);
    const modifiedSpecificInfo: IModifySpecificInfoPostObject = { id: article.id, modifiedSpecificInfo: article.specificInfo };
    articleScrapingApi.modifySpecificInfo(modifiedSpecificInfo).then((response: string) => {
        toast.add({ severity: 'success', life: 10000, summary: response });
    }).catch(() => {
        toast.add({ severity: 'error', life: 10000, summary: 'Unable to link to new project' });
    });

    additionalProjectToLink.value = null;
}

async function linkNewProject (article: IArticle): Promise<void> {
    if (!titleForNewProject.value) {
        toast.add({ severity: 'error', life: 10000, summary: 'Please enter a title for the new project' });
        return;
    }
    const projectInfo: IProjectInfo = {
        projectName: titleForNewProject.value,
        countryCode: article.articleIdentifiers.countries[0] ?? 'Unknown',
        newProjectToAdd: true,
    };
    article.specificInfo ??= [];
    article.specificInfo.push(projectInfo);
    const modifiedSpecificInfo: IModifySpecificInfoPostObject = { id: article.id, modifiedSpecificInfo: article.specificInfo };
    articleScrapingApi.modifySpecificInfo(modifiedSpecificInfo).then((response: string) => {
        toast.add({ severity: 'success', life: 10000, summary: response });
    }).catch(() => {
        toast.add({ severity: 'error', life: 10000, summary: 'Unable to link to new project' });
    });

    titleForNewProject.value = null;
}

async function deleteArticle (id: string): Promise<void> {
    articleScrapingApi.deleteItemFromArticleDb(id).then((response: string) => {
        toast.add({ severity: 'success', life: 10000, summary: response });
    }).catch(() => {
        toast.add({ severity: 'error', life: 10000, summary: 'Unable to delete article' });
    });
}

</script>

<style lang="scss" scoped>
.article {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    margin-bottom: var(--defaultSpacing);
    background-color: var(--neutral300);
    padding: var(--defaultSpacing);
    border-radius: var(--borderRadiusOuter);

    .button-holder {
        display: flex;
        gap: var(--smallSpacing);
        align-items: center;
        justify-content: space-between;
    }
    .sub-title {
        display: flex;
        gap: var(--smallSpacing);
    }
    .description {
        font-size: var(--smallFontSize);
    }
    .scraped-project {
        display: flex;
        flex-direction: column;
        gap: var(--largeSpacing);
        background-color: var(--neutral100);
        padding: var(--smallSpacing);
        border-radius: var(--borderRadiusOuter);
        font-size: var(--smallFontSize);

        .potential-match {
            margin-top: var(--defaultSpacing);
            background-color: var(--neutral300);
        }
        .different-project-options {
            display: flex;
            flex-direction: column;
            gap: var(--smallSpacing);
            padding: var(--defaultSpacing) 0;

            .different-project-selector {
                min-width: 150px;
            }
        }
        .button-holder {
            justify-content: start;
        }
    }
}

p {
    margin: 0;
}
</style>
